import { TrashIcon } from "lucide-react";

import { DecisionOptionsEditor } from "./DecisionOptionsEditor";
import { changeQuestionType, QUESTION_TYPE_HINTS, QUESTION_TYPE_LABELS } from "./utils";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DecisionQuestion, DecisionQuestionType } from "@/types/prompt-blueprint";

interface DecisionQuestionCardProps {
  question: DecisionQuestion;
  isDuplicateName?: boolean;
  canRemove?: boolean;
  onChange: (question: DecisionQuestion) => void;
  onRemove: () => void;
}

export const DecisionQuestionCard = ({
  question,
  isDuplicateName = false,
  canRemove = true,
  onChange,
  onRemove,
}: DecisionQuestionCardProps) => (
  <div className="flex flex-col gap-3 rounded-md border border-gray-200 bg-white p-3">
    <div className="flex items-center gap-2">
      <Input
        className="font-mono text-sm"
        aria-label="Question name"
        placeholder="question_name"
        value={question.name}
        onChange={e => onChange({ ...question, name: e.target.value })}
      />
      <Select
        value={question.type}
        onValueChange={value =>
          onChange(changeQuestionType(question, value as DecisionQuestionType))
        }
      >
        <SelectTrigger className="w-36 shrink-0 bg-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(QUESTION_TYPE_LABELS).map(([type, label]) => (
            <SelectItem key={type} value={type}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={!canRemove}
        aria-label="Remove question"
        onClick={onRemove}
      >
        <TrashIcon className="h-4 w-4" />
      </Button>
    </div>
    {isDuplicateName && <p className="text-xs text-red-600">Question names must be unique.</p>}
    <Textarea
      placeholder="What should the model decide about the prompt input?"
      value={question.instructions}
      onChange={e => onChange({ ...question, instructions: e.target.value })}
    />
    <p className="text-xs text-gray-500">{QUESTION_TYPE_HINTS[question.type]}</p>
    {question.type !== "binary" && (
      <DecisionOptionsEditor
        type={question.type}
        options={question.options ?? []}
        onChange={options => onChange({ ...question, options })}
      />
    )}
  </div>
);
